import { mostrarAcesso } from "@/components/AccessShare";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Loader2, User, Lock, GraduationCap, ShoppingCart, ChevronRight, Eye, CheckCircle, PauseCircle, Ban, KeyRound, Link2, Pencil, MapPin, Boxes, Copy, EyeOff, Wand2, Receipt, ExternalLink, Scissors, ThumbsUp, ThumbsDown, FlaskConical,
} from "lucide-react";
import { NucleoIcone } from "@/components/InnovaIAAluna";
import { toast } from "sonner";
import { situacao } from "@/pages/admin/SalaoGestao";
import { PersonalProfile, Card, Field } from "@/components/PersonalProfile";
import { EnrollmentsDialog, STATE_META, fmtDate } from "@/components/EnrollmentsDialog";
import { OrdersSection } from "@/pages/account/Account";
import { impersonate } from "@/components/ImpersonationBanner";
import { PersonAccess } from "@/components/AccessWidgets";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);

/** Admin: mesma tela de "Minha conta", para qualquer aluno ou professor. */
export default function PersonAdmin({ kind }) {
  const { id } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [p, setP] = useState(null);
  const isStudent = kind === "aluno";
  const listPath = isStudent ? "/admin/alunos" : "/admin/professores";

  const load = useCallback(
    () => api.get(`/admin/people/${kind}/${id}`).then((r) => setP(r.data)).catch(() => { toast.error("Cadastro não encontrado."); navigate(listPath); }),
    [kind, id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => { load(); }, [load]);

  const SECTIONS = [
    { key: "dados", label: "Dados pessoais", icon: User },
    { key: "seguranca", label: "Dados de acesso e segurança", icon: Lock },
    { key: "acessos", label: "Acessos e aparelhos", icon: MapPin },
    ...(!isStudent ? [{ key: "turmas", label: "Turmas", icon: Boxes }] : []),
    // só aparecem para professora com a permissão (hoje: Keyla Sampaio)
    ...(!isStudent && (p?.permissions || []).includes("vendas") ? [{ key: "vendas", label: "Links de venda", icon: Link2 }] : []),
    ...(!isStudent && (p?.permissions || []).includes("cobrancas") ? [{ key: "cobrancas", label: "Cobranças", icon: Receipt }] : []),
    ...(isStudent ? [
      { key: "matriculas", label: "Matrículas", icon: GraduationCap },
      { key: "compras", label: "Compras", icon: ShoppingCart },
    ] : []),
    { key: "salao", label: "IEP Salão", icon: Scissors },
    { key: "ia", label: "IEP Intelligence", icon: NucleoIcone },
    { key: "tricolab", label: "Tricolab", icon: FlaskConical },
  ];
  const h = hash.replace("#", "");
  const section = SECTIONS.some((s) => s.key === h) ? h : "dados";
  const current = SECTIONS.find((s) => s.key === section);
  const go = (key) => navigate({ hash: key }, { replace: true });

  if (!p) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;

  const subject = { ...p, role: p.role };
  const base = `/admin/people/${kind}/${id}`;
  const upload = async (img, file) => { const fd = new FormData(); fd.append("file", file); setP((await api.post(`${base}/${img}`, fd)).data); };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <button onClick={() => navigate(listPath)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Voltar para {isStudent ? "alunos" : "professores"}
        </button>
        {/* professora que também é aluna: um só botão, "Acessar como aluno" (lista de cursos e recursos da área do aluno) */}
        <Button onClick={() => (!isStudent && p.student_id ? impersonate("aluno", p.student_id, setUser, navigate) : impersonate(kind, id, setUser, navigate))} disabled={!p.has_account}
          title={p.has_account ? "" : "Disponível depois que a pessoa ativar a conta"}
          className="text-white" style={{ background: "#B45309" }} data-testid="btn-impersonate">
          <Eye className="h-4 w-4 mr-1.5" /> Acessar como {isStudent || p.student_id ? "aluno" : "professor"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
        <nav className="bg-white border border-slate-200 rounded-2xl p-2 lg:sticky lg:top-24 flex lg:block gap-1 overflow-x-auto">
          {SECTIONS.map((s) => {
            const active = s.key === section;
            return (
              <button key={s.key} onClick={() => go(s.key)}
                className={`shrink-0 lg:w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-[15px] transition-colors ${
                  active ? "text-[#1E9E2A] font-semibold bg-green-50" : "text-slate-600 hover:bg-slate-50"}`}>
                <s.icon className="h-5 w-5 shrink-0" />
                <span className="flex-1 whitespace-nowrap">{s.label}</span>
                <ChevronRight className="h-4 w-4 hidden lg:block" />
              </button>
            );
          })}
        </nav>

        <div className="space-y-6 min-w-0">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl grid place-items-center bg-green-50 text-[#1E9E2A]"><current.icon className="h-5 w-5" /></span>
            <h2 className="font-display text-2xl font-bold text-slate-900">{current.label}</h2>
          </div>

          {section === "dados" && (
            <PersonalProfile
              subject={subject}
              details={p}
              isStudent={isStudent}
              isInstructor={!isStudent}
              uploadsDisabled={p.has_account ? "" : "Foto e capa ficam disponíveis quando a pessoa ativar a conta (convite ou primeiro acesso)."}
              onSave={async (body) => setP((await api.put(base, body)).data)}
              onUpload={upload}
              onRemove={async (img) => setP((await api.delete(`${base}/${img}`)).data)}
              admin={{
                onEmailChange: async (email) => setP((await api.put(`${base}/email`, { email })).data),
                roleValue: isStudent ? "aluno" : (p.profile || "professor"),
                dual: isStudent && p.instructor_id ? { label: ({ professor: "Professor(a)", instrutor: "Instrutor(a)", mentor: "Mentor(a)" })[p.instructor_profile] || "Professor(a)", href: `/admin/professores/${p.instructor_id}` } : null,
                status: p.access_status || "ativo",
                onRoleChange: async (role) => {
                  const toStudent = role === "aluno";
                  const moving = toStudent !== isStudent;
                  const msg = moving
                    ? (toStudent
                        ? `Transformar ${p.name} em ALUNO? Ele(a) sai das turmas em que é professor(a) e precisará entrar de novo.`
                        : (p.has_account
                            ? `Tornar ${p.name} ${role.toUpperCase()}? Ela(e) ganha a área de professor(a) e CONTINUA aluna: mesmo login, matrículas, progresso, certificados e turmas. Precisará entrar de novo.`
                            : `Tornar ${p.name} ${role.toUpperCase()}? Como ainda não ativou a conta, o cadastro de aluno é substituído pelo de professor(a) (matrículas e turmas de aluno são removidas).`))
                    : `Alterar o perfil de ${p.name} para ${role}?`;
                  if (!(await confirmar(msg))) return;
                  try {
                    const r = await api.post(`/admin/people-role/${kind}/${id}`, { role });
                    toast.success("Perfil atualizado.");
                    if (moving) navigate(`/admin/${r.data.kind === "aluno" ? "alunos" : "professores"}/${r.data.id}`, { replace: true });
                    else load();
                  } catch (e) { toast.error(errMsg(e)); }
                },
                onStatusChange: async (status) => {
                  if (status === "desativado" && !(await confirmar(`Desativar ${p.name}? A pessoa não conseguirá entrar até ser ativada de novo.`))) return;
                  try {
                    setP((await api.put(`/admin/people/${kind}/${id}/status`, { status })).data);
                    toast.success(status === "ativo" ? "Cadastro ativado." : "Cadastro desativado.");
                  } catch (e) { toast.error(errMsg(e)); }
                },
              }}
            />
          )}
          {section === "seguranca" && <AccessSection p={p} kind={kind} id={id} onChange={setP} reload={load} />}
          {section === "acessos" && <PersonAccess kind={kind} id={id} />}
          {section === "turmas" && !isStudent && <InstructorGroups p={p} />}
          {(section === "vendas" || section === "cobrancas") && !isStudent && <PersonSelling kind={kind} id={id} mode={section} />}
          {section === "matriculas" && <StudentEnrollments p={p} />}
          {section === "tricolab" && <TricolabPessoa kind={kind} id={id} />}
          {section === "ia" && (isStudent ? <IAAluna id={id} /> : <IAProfessora id={id} />)}
          {section === "salao" && <><SalaoUso kind={kind} id={id} /><SalaoCard p={p} kind={kind} id={id} onChange={setP} /></>}
          {section === "compras" && (p.has_account ? <OrdersSection url={`${base}/orders`} admin /> : <Card><p className="text-sm text-slate-500">Nenhuma compra (conta ainda não ativada).</p></Card>)}
        </div>
      </div>
    </div>
  );
}

const reaisIA = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const COBRANCA = { experimente: "experimentar", gratis: "grátis", credito: "crédito", teste: "teste do admin", sem_custo: "não contou" };
const PEDIDO = { approved: "Paga", pending: "Pendente", in_process: "Em análise", rejected: "Recusada", cancelled: "Cancelada", refunded: "Estornada", charged_back: "Chargeback", mp_error: "Erro" };

/** App Tricolab: ativar para esta pessoa (enquanto não está liberado para todas). */
function TricolabPessoa({ kind, id }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const carregar = useCallback(() => api.get(`/admin/tricolab/pessoa/${kind}/${id}`).then((r) => setD(r.data)).catch((e) => setD({ erro: errMsg(e) })), [kind, id]);
  useEffect(() => { carregar(); }, [carregar]);
  if (!d) return <Card title="Tricolab"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>;
  if (d.erro) return <Card title="Tricolab"><p className="text-sm text-red-600">{d.erro}</p></Card>;
  const ativar = async (v) => {
    setBusy(true);
    try { await api.put(`/admin/tricolab/pessoa/${kind}/${id}`, { ativo: v }); toast.success(v ? "Tricolab ativado para esta pessoa." : "Tricolab desativado."); carregar(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Tricolab">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <Switch checked={!!d.ativo} disabled={busy || !d.tem_conta} onCheckedChange={ativar} />
        <div className="text-sm"><b>{d.ativo ? "Ativado" : "Ativar o app Tricolab"}</b>
          <div className="text-xs text-slate-500">{d.tem_conta ? "A pessoa abre em iepplataforma.com/tricolab (e pode instalar como app). Só vê as próprias clientes." : "Ainda não ativou a conta (sem login)."}</div></div>
      </div>
      <div className="text-sm text-slate-600">{d.clientes} cliente(s) cadastrada(s) · {d.checkups} check-up(s)</div>
    </Card>
  );
}

/** IEP Intelligence da professora: ativar na área dela (perguntas ilimitadas, sem cobrança) e as perguntas que ela fez. */
function IAProfessora({ id }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [abertas, setAbertas] = useState({});
  const carregar = useCallback(() => api.get(`/admin/innova-ia/professora/${id}`).then((r) => setD(r.data)).catch((e) => setD({ erro: errMsg(e) })), [id]);
  useEffect(() => { carregar(); }, [carregar]);
  if (!d) return <Card title="IEP Intelligence"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>;
  if (d.erro) return <Card title="IEP Intelligence"><p className="text-sm text-red-600">{d.erro}</p></Card>;
  const ativar = async (v) => {
    setBusy(true);
    try { await api.put(`/admin/innova-ia/professora/${id}`, { ativo: v }); toast.success(v ? "IEP Intelligence ativada para esta professora." : "IEP Intelligence desativada para esta professora."); carregar(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Card title="IEP Intelligence">
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <Switch checked={!!d.ativa} disabled={busy || !d.tem_conta} onCheckedChange={ativar} />
          <div className="text-sm"><b>{d.ativa ? "Ativada para esta professora" : "Ativar para esta professora"}</b>
            <div className="text-xs text-slate-500">{d.tem_conta ? "O botão “IEP Intelligence” e a aba “Dúvida sobre esta aula” aparecem na área de professora. Perguntas ilimitadas, sem cobrança." : "Ela ainda não ativou a conta (sem login)."}</div></div>
        </div>
      </Card>
      <Card title={`Perguntas (${d.total})`}>
        {d.perguntas.length === 0 ? <p className="text-sm text-slate-500">Ainda não fez perguntas.</p> : (
          <ul className="divide-y divide-slate-100">{d.perguntas.map((x) => (
            <li key={x.id} className="py-2">
              <button className="w-full text-left flex items-start gap-2" onClick={() => setAbertas((a) => ({ ...a, [x.id]: !a[x.id] }))}>
                <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 text-slate-400 transition-transform ${abertas[x.id] ? "rotate-90" : ""}`} />
                <span className="flex-1 text-sm text-slate-800">{x.pergunta}</span>
                <span className="text-[11px] text-slate-400 shrink-0">{fmtDia(x.at)}</span>
              </button>
              {abertas[x.id] && <p className="ml-6 mt-1 text-sm text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-3">{x.resposta}</p>}
            </li>))}</ul>)}
      </Card>
    </>
  );
}

/** IEP Intelligence da aluna: saldo, uso, perguntas, pacotes comprados, dúvidas e bônus de créditos. */
function IAAluna({ id }) {
  const [d, setD] = useState(null);
  const [qtd, setQtd] = useState("10");
  const [motivo, setMotivo] = useState("Cortesia IEP");
  const [busy, setBusy] = useState(false);
  const [abertas, setAbertas] = useState({});
  const carregar = useCallback(() => api.get(`/admin/innova-ia/aluna/${id}`).then((r) => setD(r.data)).catch((e) => setD({ erro: errMsg(e) })), [id]);
  useEffect(() => { carregar(); }, [carregar]);
  if (!d) return <Card title="IEP Intelligence"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>;
  if (d.erro) return <Card title="IEP Intelligence"><p className="text-sm text-red-600">{d.erro}</p></Card>;
  const ajustar = async (sinal) => {
    const n = Math.abs(Number(qtd) || 0) * sinal;
    if (!n) return;
    if (!(await confirmar(sinal > 0 ? `Dar ${Math.abs(n)} perguntas de bônus para esta aluna?` : `Tirar ${Math.abs(n)} créditos desta aluna?`))) return;
    setBusy(true);
    try { await api.post(`/admin/innova-ia/aluna/${id}/creditos`, { qtd: n, motivo }); toast.success(sinal > 0 ? "Bônus adicionado." : "Créditos retirados."); carregar(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const testadora = async (v) => {
    setBusy(true);
    try { await api.put(`/admin/innova-ia/aluna/${id}/testadora`, { ativo: v }); toast.success(v ? "IEP Intelligence ativada para esta aluna." : "IEP Intelligence desativada para esta aluna."); carregar(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const Info = ({ label, children }) => <div><div className="text-xs text-slate-500">{label}</div><div className="text-lg font-display font-semibold text-slate-900">{children}</div></div>;
  const duvidas = d.perguntas.filter((x) => x.duvida);
  return (
    <>
      <Card title="IEP Intelligence">
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <Switch checked={!!d.testadora} disabled={busy} onCheckedChange={testadora} />
          <div className="text-sm"><b>{d.testadora ? "Ativada para esta aluna" : "Ativar para esta aluna"}</b><div className="text-xs text-slate-500">Ela vê a IEP Intelligence mesmo antes do lançamento (gasta as grátis e pode comprar).</div></div>
          <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${d.ve_a_ia ? "bg-green-100 text-[#1E9E2A]" : "bg-slate-200 text-slate-600"}`}>{d.nivel === "experimente" ? "Experimentando (certificado)" : d.ve_a_ia ? "Vê a IA" : "Não vê a IA"}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Info label={d.periodo === "experimente" ? "Para experimentar" : `Grátis ${d.periodo === "mes" ? "este mês" : "(total)"}`}>{d.gratis_restantes} de {d.gratis_total}</Info>
          <Info label="Créditos (comprados + bônus)">{d.creditos}</Info>
          <Info label="Perguntas feitas">{d.total}</Info>
          <Info label="Última pergunta">{d.ultima ? fmtDia(d.ultima) : "—"}</Info>
        </div>
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="text-xs font-semibold text-slate-500 mb-2">Bônus ou retirada de créditos</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} className="w-24" />
            <Input value={motivo} maxLength={200} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo" className="w-56" />
            <Button size="sm" disabled={busy} onClick={() => ajustar(1)} className="text-white" style={{ background: "#1E9E2A" }}>+ Dar bônus</Button>
            <Button size="sm" variant="outline" disabled={busy || !d.creditos} onClick={() => ajustar(-1)}>Tirar créditos</Button>
          </div>
          {d.ajustes.length > 0 && <ul className="mt-3 space-y-1 text-xs text-slate-600">{d.ajustes.map((a, i) => (
            <li key={i}><b className={a.qtd > 0 ? "text-[#1E9E2A]" : "text-red-600"}>{a.qtd > 0 ? "+" : ""}{a.qtd}</b> · {a.motivo} · {a.por} · {fmtDia(a.em)}</li>))}</ul>}
        </div>
      </Card>
      <Card title="Pacotes comprados">
        {d.pacotes.length === 0 ? <p className="text-sm text-slate-500">Nenhum pacote comprado.</p> : (
          <ul className="divide-y divide-slate-100 text-sm">{d.pacotes.map((o) => (
            <li key={o.id} className="py-2 flex flex-wrap items-center gap-3"><span className="font-semibold">{o.ia_qtd} perguntas</span><span>{reaisIA(o.amount)}</span>
              <span className="text-slate-500">{fmtDia(o.paid_at || o.created_at)}</span>
              <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${o.status === "approved" ? "bg-green-100 text-[#1E9E2A]" : "bg-slate-100 text-slate-600"}`}>{PEDIDO[o.status] || o.status}</span></li>))}</ul>)}
      </Card>
      {duvidas.length > 0 && (
        <Card title="Dúvidas enviadas à professora">
          <ul className="space-y-2 text-sm">{duvidas.map((x) => (
            <li key={x.id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2"><b className="text-slate-800">{x.pergunta}</b>
                <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${x.duvida.status === "aberta" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-[#1E9E2A]"}`}>{x.duvida.status === "aberta" ? "Aguardando professora" : "Respondida"}</span></div>
              <div className="text-xs text-slate-500 mt-1">{x.duvida.turma ? `Turma ${x.duvida.turma}` : "Equipe do IEP"} · {fmtDia(x.duvida.em)}</div>
              {x.duvida.resposta && <p className="mt-1 text-slate-700"><b className="text-[#1E9E2A]">{x.duvida.respondida_por || "Professora"}:</b> {x.duvida.resposta}</p>}
            </li>))}</ul>
        </Card>
      )}
      <Card title={`Perguntas (${d.total})`}>
        {d.perguntas.length === 0 ? <p className="text-sm text-slate-500">Ainda não fez perguntas.</p> : (
          <ul className="divide-y divide-slate-100">{d.perguntas.map((x) => (
            <li key={x.id} className="py-2">
              <button className="w-full text-left flex items-start gap-2" onClick={() => setAbertas((a) => ({ ...a, [x.id]: !a[x.id] }))}>
                <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 text-slate-400 transition-transform ${abertas[x.id] ? "rotate-90" : ""}`} />
                <span className="flex-1 text-sm text-slate-800">{x.pergunta}</span>
                {x.nota_aluna === "bom" && <ThumbsUp className="h-4 w-4 text-[#1E9E2A] shrink-0" />}
                {x.nota_aluna === "ruim" && <ThumbsDown className="h-4 w-4 text-red-500 shrink-0" />}
                <span className="text-[11px] text-slate-400 shrink-0">{fmtDia(x.at)} · {x.trava_prova ? "trava da prova" : COBRANCA[x.cobranca] || ""}</span>
              </button>
              {abertas[x.id] && <p className="ml-6 mt-1 text-sm text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-3">{x.resposta}</p>}
            </li>))}</ul>)}
      </Card>
    </>
  );
}

// IEP Salão: não é aberto para todas — o admin libera por campanha
const fmtDia = (iso) => (iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("pt-BR") : "—");

/** O que interessa ao admin sobre o salão desta pessoa (mesmos dados da página IEP Salão, sem faturamento do salão). */
function SalaoUso({ kind, id }) {
  const [d, setD] = useState(null);
  const [dias, setDias] = useState("30");
  const [motivo, setMotivo] = useState("Cortesia IEP");
  const [busy, setBusy] = useState(false);
  const carregar = useCallback(() => {
    api.get("/admin/salao/gestao").then((r) => setD({ l: (r.data.linhas || []).find((x) => x.kind === kind && x.person_id === id) || null, url: r.data.salao_url, ok: r.data.salao_ok }))
      .catch(() => setD({ erro: true }));
  }, [kind, id]);
  useEffect(() => { carregar(); }, [carregar]);
  if (!d) return <Card title="Uso do salão"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Card>;
  if (d.erro || d.ok === false) return <Card title="Uso do salão"><p className="text-sm text-red-600">Não consegui falar com o IEP Salão agora. Tente de novo em instantes.</p></Card>;
  const l = d.l;
  if (!l) return <Card title="Uso do salão"><p className="text-sm text-slate-500">Esta pessoa ainda não foi liberada nem comprou o IEP Salão.</p></Card>;
  const s = situacao(l);
  const darDias = async () => {
    setBusy(true);
    try {
      await api.post("/admin/salao/dias", { user_id: l.user_id, dias: Number(dias), motivo });
      toast.success(`${dias} dias adicionados.`); carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const excluir = async () => {
    const nome = window.prompt(`Excluir o salão "${l.salao.nome}"?\n\nO salão, os serviços, a equipe, as clientes, a agenda e o caixa saem do sistema (ficam arquivados para restaurar, se precisar). A aluna continua com o acesso e pode criar outro salão.\n\nPara confirmar, digite o nome do salão:`);
    if (nome === null) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/salao/excluir", { user_id: l.user_id, confirmacao: nome });
      toast.success(`Salão "${r.data.salao}" excluído.`); carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const vitrine = async (ligar) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/salao/vitrine-ligar", { user_id: l.user_id, ligar });
      toast.success(!ligar ? "Vitrine desligada." : r.data.falta.length ? "Vitrine ligada, mas ainda falta algo (veja abaixo)." : "Pronto: o salão já aparece na vitrine.");
      carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const Info = ({ label, children }) => <div><div className="text-xs text-slate-500">{label}</div><div className="text-sm font-medium text-slate-800">{children}</div></div>;
  const falta = l.salao?.vitrine_falta || [];
  return (
    <Card title="Uso do salão">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-display text-lg font-semibold text-slate-900">{l.salao?.nome || "Salão ainda não criado"}</div>
          {l.salao && <div className="text-sm text-slate-500">{[l.salao.cidade, l.salao.uf].filter(Boolean).join("/") || "Cidade não informada"}{l.salao.vitrine && <span className="ml-2 text-xs font-semibold text-[#926B38]">· na vitrine</span>}</div>}
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.c}`}>{s.t}</span>
      </div>
      {l.selo && (
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-green-50 border border-green-100 p-2.5">
          <img src={l.selo.url} alt="" className="h-14 w-14 shrink-0" />
          <div><div className="text-xs text-slate-500">Selo (automático, pelo maior certificado)</div><div className="text-sm font-semibold text-[#1E9E2A]">{l.selo.nome}</div></div>
          <a href={l.selo.url} target="_blank" rel="noopener" className="ml-auto text-sm font-semibold text-[#1E9E2A] hover:underline">Abrir</a>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Info label="Plano">{l.plano === "pro" ? "Pro" : "Básico"}</Info>
        <Info label="Acesso até">{l.acesso?.ate ? fmtDia(l.acesso.ate) : "—"}{l.acesso?.ativo && <span className="block text-xs font-normal text-slate-400">{l.acesso.dias_restantes} dias restantes</span>}</Info>
        <Info label="Liberado em">{fmtDia(l.liberado_em)}</Info>
        <Info label="Último acesso">{fmtDia(l.ultimo_login)}</Info>
      </div>
      {l.salao && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[["Horários (30 dias)", l.uso.horarios_30d], ["Pelo link", l.uso.online_30d], ["Pela vitrine", l.uso.vitrine_30d], ["Comandas (30 dias)", l.uso.comandas_30d], ["Clientes", l.uso.clientes]].map(([t, v]) => (
            <div key={t} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5"><div className="text-[11px] text-slate-500">{t}</div><div className="font-display text-xl font-bold text-[#232D4F]">{v || 0}</div></div>
          ))}
        </div>
      )}
      {l.salao && (
        <div className="mt-4 rounded-lg border border-slate-200 p-3" data-testid="salao-vitrine">
          <div className="flex items-center gap-3">
            <Switch checked={!!l.salao.vitrine} disabled={busy} onCheckedChange={vitrine} />
            <div className="flex-1 text-sm"><b>Aparecer na vitrine de profissionais</b>
              <div className="text-xs text-slate-500">A aluna também pode ligar em Meu salão. Ao ligar, se ela trabalha sozinha, os serviços do link já ficam marcados para ela.</div></div>
          </div>
          {l.salao.vitrine && (falta.length === 0
            ? <p className="mt-2 text-sm font-semibold text-[#1E9E2A]">Aparecendo na vitrine.</p>
            : <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"><b>Ainda não aparece. Falta:</b><ul className="list-disc pl-5">{falta.map((x) => <li key={x}>{x}</li>)}</ul></div>)}
        </div>
      )}
      {l.salao?.slug && d.url && (
        <a href={`${d.url.replace(/\/$/, "")}/s/${l.salao.slug}`} target="_blank" rel="noopener" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1E9E2A] hover:underline">
          Ver página de agendamento do salão <ExternalLink className="h-3.5 w-3.5" /></a>
      )}
      {l.salao && (
        <button onClick={excluir} disabled={busy} className="mt-3 ml-4 inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline" data-testid="salao-excluir">
          Excluir salão</button>
      )}
      {l.compras?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-1">Pagamentos ao IEP (combos com salão)</div>
          <ul className="text-sm text-slate-700 space-y-0.5">
            {l.compras.map((c) => <li key={c.order_id}>{fmtDia(c.data)} · {c.combo} · {c.meses} {c.meses === 1 ? "mês" : "meses"} · {c.plano === "pro" ? "Pro" : "Básico"} · {Number(c.valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} · {c.status === "approved" ? "aprovado" : c.status}</li>)}
          </ul>
        </div>
      )}
      {l.entrou && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-end gap-2 flex-wrap">
          <div className="space-y-1"><label className="text-xs text-slate-500">Dar dias de acesso</label><Input type="number" min={1} max={730} value={dias} onChange={(e) => setDias(e.target.value)} className="w-24" /></div>
          <div className="space-y-1 flex-1 min-w-[180px]"><label className="text-xs text-slate-500">Motivo (ela vê em Benefícios)</label><Input value={motivo} maxLength={120} onChange={(e) => setMotivo(e.target.value)} /></div>
          <Button disabled={busy || !(Number(dias) >= 1)} onClick={darDias} className="text-white" style={{ background: "#1E9E2A" }}>{busy ? "Salvando…" : "+ dias"}</Button>
        </div>
      )}
    </Card>
  );
}

function SalaoCard({ p, kind, id, onChange }) {
  const [campanha, setCampanha] = useState(p.salao_campanha || "");
  const [plano, setPlano] = useState(p.salao_plano || "basico");
  const [busy, setBusy] = useState(false);
  const salvar = async (liberado) => {
    setBusy(true);
    try {
      onChange((await api.put(`/admin/people/${kind}/${id}/salao`, { liberado, campanha, plano })).data);
      toast.success(liberado ? "IEP Salão liberado. O botão \"Meu Salão\" aparece no menu da pessoa." : "Acesso ao IEP Salão retirado.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Card title="IEP Salão">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-slate-800">Liberar o sistema do salão</div>
          <p className="text-sm text-slate-500 mt-0.5">Só quem você liberar vê o botão "Meu Salão" e consegue entrar no sistema com a conta do IEP.
            {p.salao_liberado && p.salao_liberado_em ? ` Liberado em ${new Date(p.salao_liberado_em).toLocaleDateString("pt-BR")}.` : ""}
            {!p.has_account ? " A pessoa precisa fazer o 1º acesso antes." : ""}</p>
        </div>
        <Switch checked={!!p.salao_liberado} disabled={busy || !p.has_account} data-testid="salao-liberado" onCheckedChange={salvar} />
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-xl">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Campanha (opcional)</label>
          <Input value={campanha} maxLength={80} placeholder="Ex.: Formandas Fortaleza out/2026" onChange={(e) => setCampanha(e.target.value)}
            onBlur={() => { if (p.salao_liberado && campanha !== (p.salao_campanha || "")) salvar(true); }} data-testid="salao-campanha" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Plano</label>
          <select value={plano} onChange={(e) => setPlano(e.target.value)} onBlur={() => { if (p.salao_liberado && plano !== (p.salao_plano || "basico")) salvar(true); }}
            className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="salao-plano">
            <option value="basico">Básico (1 profissional)</option>
            <option value="pro">Pro (até 5 profissionais + comissões)</option>
          </select>
        </div>
      </div>
      {(p.salao_creditos || []).length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-1">Compras com IEP Salão</div>
          <ul className="text-sm text-slate-700 space-y-0.5">
            {p.salao_creditos.map((c) => <li key={c.order_id}>{new Date(c.em).toLocaleDateString("pt-BR")} · {c.campanha} · {c.meses} {c.meses === 1 ? "mês" : "meses"} · plano {c.plano === "pro" ? "Pro" : "Básico"}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}

function AccessSection({ p, kind, id, onChange, reload }) {
  const [pw, setPw] = useState({ password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState("");
  const [until, setUntil] = useState("");
  const isStudent = kind === "aluno";
  const access = p.access_status || "ativo";

  const setPassword = async (e) => {
    e.preventDefault();
    if (pw.password.length < 8) return toast.error("A senha precisa ter pelo menos 8 caracteres.");
    setBusy("pw");
    try {
      onChange((await api.post(`/admin/people/${kind}/${id}/password`, { password: pw.password })).data);
      toast.success((p.has_account ? "Senha atualizada!" : "Acesso criado com a senha definida!") + " Copie e envie para a pessoa, se precisar.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const setStatus = async (status) => {
    setBusy(status);
    try {
      await api.put(`/students/${id}/status`, { status, until: status === "suspenso" ? until : "" });
      toast.success({ ativo: "Aluno ativado.", suspenso: "Aluno suspenso.", bloqueado: "Aluno bloqueado." }[status]);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const invite = async () => {
    try {
      const r = await api.post(`/${isStudent ? "students" : "instructors"}/${id}/resend-invite`);
      mostrarAcesso(r.data);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const accessLabel = access === "desativado" ? "Desativado" : access === "bloqueado" ? "Bloqueado" : access === "suspenso"
    ? (p.suspended_until ? `Suspenso até ${fmtDate(p.suspended_until)}` : "Suspenso") : "Ativo";

  return (
    <>
      <Card title="Acesso">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="E-mail de login" className="sm:col-span-2"><Input value={p.email} readOnly /></Field>
          <Field label="Conta"><Input value={p.status === "primeiro_acesso" ? "Aguardando 1º acesso" : p.has_account ? "Ativada" : "Sem acesso criado"} readOnly /></Field>
          <Field label="Aceite do contrato e privacidade" className="sm:col-span-3">
            <Input readOnly value={p.terms_accepted_at ? `Aceito em ${new Date(p.terms_accepted_at).toLocaleString("pt-BR")} (versão ${p.terms_version})` : "Ainda não aceitou"} data-testid="person-terms" />
          </Field>
        </div>
        {(!p.has_account || p.status === "primeiro_acesso") && (
          <div className="mt-4 flex items-center gap-3 flex-wrap text-sm text-slate-500">
            {p.has_account ? "A pessoa ainda não fez o 1º acesso." : "A pessoa ainda não tem acesso."} Envie um código de 1º acesso por e-mail.
            <Button size="sm" variant="outline" onClick={invite}><Link2 className="h-4 w-4 mr-1.5" /> Enviar acesso (novo código)</Button>
          </div>
        )}
      </Card>

      {isStudent && (
        <Card title="Situação do aluno">
          <p className="text-sm text-slate-600 mb-3">Situação atual: <strong>{accessLabel}</strong>{p.access_reason ? ` — ${p.access_reason}` : ""}</p>
          <div className="flex flex-wrap items-end gap-2">
            <Button variant="outline" className="text-green-700" disabled={!!busy || access === "ativo"} onClick={() => setStatus("ativo")}><CheckCircle className="h-4 w-4 mr-1.5" /> Ativar</Button>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Suspender até (opcional)</label>
                <Input type="date" value={until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} className="h-9 w-40" />
              </div>
              <Button variant="outline" className="text-amber-700" disabled={!!busy} onClick={() => setStatus("suspenso")}><PauseCircle className="h-4 w-4 mr-1.5" /> Suspender</Button>
            </div>
            <Button variant="outline" className="text-red-600" disabled={!!busy || access === "bloqueado"} onClick={() => setStatus("bloqueado")}><Ban className="h-4 w-4 mr-1.5" /> Bloquear</Button>
          </div>
        </Card>
      )}

      {!isStudent && (
        <Card title="Permissões">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-slate-800">Pode cadastrar e matricular alunos</div>
              <p className="text-sm text-slate-500 mt-0.5">Na área do professor aparece a aba "Alunos": ele cadastra alunos novos (com código de 1º acesso),
                reenvia o acesso e matricula apenas nas turmas que conduz e nos cursos em que atua. Não exclui, não bloqueia e não altera configurações.</p>
            </div>
            <Switch checked={(p.permissions || []).includes("cadastro")} data-testid="perm-register"
              onCheckedChange={async (v) => {
                try {
                  onChange((await api.put(`/admin/people/${kind}/${id}/permissions`, { can_register: v })).data);
                  toast.success(v ? "Permissão de cadastro ativada." : "Permissão de cadastro removida.");
                } catch (e) { toast.error(errMsg(e)); }
              }} />
          </div>
          <div className="flex items-start justify-between gap-4 mt-5 pt-5 border-t border-slate-100">
            <div>
              <div className="font-medium text-slate-800">Pode gerar cobranças para alunas</div>
              <p className="text-sm text-slate-500 mt-0.5">Na área do professor aparece a aba "Cobranças": ele(a) gera links de pagamento (Pix ou cartão) e envia por e-mail/WhatsApp. Ao pagar, a aluna é matriculada. Professora CEO cobra qualquer aluno e curso; as demais, só alunas das turmas e cursos delas.</p>
            </div>
            <Switch checked={(p.permissions || []).includes("cobrancas")} data-testid="perm-charge"
              onCheckedChange={async (v) => {
                try {
                  onChange((await api.put(`/admin/people/${kind}/${id}/permissions`, { can_charge: v })).data);
                  toast.success(v ? "Cobranças liberadas para este(a) professor(a)." : "Cobranças removidas para este(a) professor(a).");
                } catch (e) { toast.error(errMsg(e)); }
              }} />
          </div>
          <div className="flex items-start justify-between gap-4 mt-5 pt-5 border-t border-slate-100">
            <div>
              <div className="font-medium text-slate-800">Pode usar os links de venda dos cursos</div>
              <p className="text-sm text-slate-500 mt-0.5">Na área do professor aparece a aba "Links de venda" com o link de cada curso em que atua (que você tenha ligado em Financeiro → Links de venda). Cada venda fica registrada no nome dele(a).</p>
            </div>
            <Switch checked={(p.permissions || []).includes("vendas")} data-testid="perm-sell"
              onCheckedChange={async (v) => {
                try {
                  onChange((await api.put(`/admin/people/${kind}/${id}/permissions`, { can_sell: v })).data);
                  toast.success(v ? "Links de venda liberados para este(a) professor(a)." : "Links de venda removidos para este(a) professor(a).");
                } catch (e) { toast.error(errMsg(e)); }
              }} />
          </div>
          <div className="flex items-start justify-between gap-4 mt-5 pt-5 border-t border-slate-100">
            <div>
              <div className="font-medium text-slate-800">Pode criar links de oferta individual</div>
              <p className="text-sm text-slate-500 mt-0.5">Em "Links de venda" aparece o botão "Criar oferta individual": um link com valor especial para uma aluna, que deixa de funcionar sozinho quando atinge o número de compras escolhido (padrão 1). Evita que a oferta seja repassada. Precisa também da permissão de links de venda.</p>
            </div>
            <Switch checked={(p.permissions || []).includes("ofertas")} data-testid="perm-offer"
              onCheckedChange={async (v) => {
                try {
                  onChange((await api.put(`/admin/people/${kind}/${id}/permissions`, { can_offer: v })).data);
                  toast.success(v ? "Links de oferta liberados." : "Links de oferta removidos.");
                } catch (e) { toast.error(errMsg(e)); }
              }} />
          </div>
        </Card>
      )}


      <form onSubmit={setPassword}>
        <Card title={p.has_account ? "Definir nova senha" : "Criar acesso com senha"}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nova senha">
              <div className="flex gap-2">
                <Input type={showPw ? "text" : "password"} autoComplete="new-password" minLength={8} value={pw.password} onChange={(e) => setPw({ password: e.target.value })} required data-testid="admin-new-password" />
                <Button type="button" variant="outline" size="icon" onClick={() => setShowPw((v) => !v)} title={showPw ? "Ocultar" : "Mostrar"}>{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
              </div>
            </Field>
            <div className="flex items-end gap-2 flex-wrap">
              <Button type="button" variant="outline" onClick={() => { const c = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"; const a = new Uint32Array(10); crypto.getRandomValues(a); setPw({ password: Array.from(a, (n) => c[n % c.length]).join("") }); setShowPw(true); }}><Wand2 className="h-4 w-4 mr-1.5" /> Gerar senha</Button>
              <Button type="button" variant="outline" disabled={!pw.password} onClick={() => { navigator.clipboard?.writeText(pw.password); toast.success("Senha copiada!"); }}><Copy className="h-4 w-4 mr-1.5" /> Copiar</Button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
            <p className="text-xs text-slate-400">Mínimo de 8 caracteres. Por segurança a senha atual não pode ser vista (fica criptografada); aqui você define uma nova, que passa a valer na hora.</p>
            <Button type="submit" disabled={!!busy} className="text-white" style={{ background: "#232D4F" }}>
              {busy === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Salvar senha</>}
            </Button>
          </div>
        </Card>
      </form>
    </>
  );
}

/** Professor(a) / Representante Credenciado: turmas ligadas a esta pessoa e onde ligar uma nova. */
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

function InstructorGroups({ p }) {
  const navigate = useNavigate();
  const [terr, setTerr] = useState(null);
  const [groups, setGroups] = useState(null);
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const isRep = p.profile === "representante";
  const load = useCallback(() => {
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
    api.get("/instructors").then((r) => { setPeople(r.data); setTerr((cur) => cur ?? (r.data.find((i) => i.id === p.id)?.territory_states || [])); }).catch(() => setPeople([]));
  }, [p.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (g, patch, okMsg) => {
    setBusy(true);
    try {
      await api.put(`/groups/${g.id}`, {
        name: g.name, description: g.description || "",
        instructor_person_id: g.instructor_person_id || "",
        representative_person_id: g.representative_person_id || "", ...patch,
      });
      toast.success(okMsg); setPick(""); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (groups === null) return <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const ceoIds = new Set(people.filter((i) => i.ceo).map((i) => i.id));
  const mine = groups.filter((g) => g.instructor_person_id === p.id || g.representative_person_id === p.id);
  const options = groups.filter((g) => (isRep ? ceoIds.has(g.instructor_person_id) && g.representative_person_id !== p.id : g.instructor_person_id !== p.id));

  const saveTerr = async () => {
    setBusy(true);
    try { await api.put(`/instructors/${p.id}/territory`, { states: terr || [] }); toast.success("Estados de atuação salvos."); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Card>
      <div className="mb-5 p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="person-territory">
        <p className="text-sm font-semibold text-slate-800">Estados de atuação</p>
        <p className="text-xs text-slate-500 mb-2">A escola escolhe em quais estados {isRep ? "o representante" : "a professora"} pode atuar. Alunas desses estados que estiverem na turma da professora CEO podem ser migradas para {isRep ? "a turma dele" : "a turma dela"} (Turmas → Reorganizar por região).</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {UFS.map((u) => {
            const on = (terr || []).includes(u);
            return <button key={u} type="button" onClick={() => setTerr((cur) => (cur || []).includes(u) ? cur.filter((x) => x !== u) : [...(cur || []), u])}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${on ? "bg-[#1E9E2A] text-white border-[#1E9E2A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>{u}</button>;
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" type="button" onClick={() => setTerr([...UFS])}>Todo o Brasil</Button>
          <Button size="sm" variant="outline" type="button" onClick={() => setTerr([])}>Limpar</Button>
          <Button size="sm" disabled={busy || terr === null} onClick={saveTerr} className="text-white" style={{ background: "#232D4F" }}>Salvar estados</Button>
          {terr && terr.length === UFS.length && <span className="text-xs text-[#1E9E2A] font-semibold">Atua em todo o Brasil</span>}
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        {isRep
          ? "Representante Credenciado: ligue a uma turma da professora CEO. A professora continua a mesma; o representante passa a pagar a taxa de bônus das alunas da turma e a receber a nota fiscal."
          : "Turmas em que esta pessoa é a professora. A turma também pode ser editada na tela Turmas."}
      </p>
      {mine.length === 0 ? <p className="text-sm text-slate-400 mb-4">Nenhuma turma ligada ainda.</p> : (
        <div className="divide-y divide-slate-100 mb-4">
          {mine.map((g) => {
            const asRep = g.representative_person_id === p.id;
            return (
              <div key={g.id} className="py-3 flex items-center gap-3 flex-wrap">
                <Boxes className="h-5 w-5 text-[#1E9E2A]" />
                <div className="flex-1 min-w-0">
                  <button type="button" onClick={() => navigate(`/admin/turmas/${g.id}`)} className="font-medium text-slate-800 hover:text-[#1E9E2A] text-left">{g.name}</button>
                  <div className="text-xs text-slate-400">
                    {asRep ? `Representante · Professora: ${g.instructor_name || "—"}` : `Professora${g.representative_name ? ` · Representante: ${g.representative_name}` : ""}`} · {g.student_count} alunos
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/turmas/${g.id}`)} data-testid={`ver-alunas-${g.id}`}>
                  Ver alunas
                </Button>
                <Button size="sm" variant="outline" disabled={busy} className="text-red-600"
                  onClick={async () => (await confirmar(asRep ? `Tirar ${p.name} como representante de "${g.name}"?` : `Tirar ${p.name} como professora de "${g.name}"?`))
                    && save(g, asRep ? { representative_person_id: "" } : { instructor_person_id: "" }, "Turma desligada.")}>
                  Desligar
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="space-y-1 flex-1 min-w-[240px]">
          <label className="text-xs text-slate-500">{isRep ? "Ligar a uma turma da professora CEO" : "Ligar como professora de uma turma"}</label>
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="person-group-select">
            <option value="">— escolha a turma —</option>
            {options.map((g) => <option key={g.id} value={g.id}>{g.name}{g.instructor_name ? ` · ${g.instructor_name}` : ""}</option>)}
          </select>
        </div>
        <Button disabled={!pick || busy} className="text-white" style={{ background: "#1E9E2A" }}
          onClick={() => { const g = groups.find((x) => x.id === pick); if (g) save(g, isRep ? { representative_person_id: p.id } : { instructor_person_id: p.id }, "Turma ligada!"); }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ligar"}
        </Button>
      </div>
      {isRep && options.length === 0 && <p className="text-xs text-slate-400 mt-2">Não há turma disponível: o representante só pode ser ligado a turmas da professora CEO.</p>}
    </Card>
  );
}

function StudentEnrollments({ p }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get(`/students/${p.id}/enrollments`).then((r) => setItems(r.data.items.filter((i) => i.enrolled))).catch(() => setItems([])), [p.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-sm text-slate-500">Cursos, datas de acesso e origem (direta ou turma).</p>
        <Button onClick={() => setOpen(true)} className="text-white" style={{ background: "#232D4F" }}><Pencil className="h-4 w-4 mr-1.5" /> Gerenciar matrículas</Button>
      </div>
      {items === null ? (
        <div className="py-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400">Nenhuma matrícula.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((i) => {
            const meta = STATE_META[i.state] || STATE_META.ativa;
            return (
              <div key={i.course_id} className="py-3 flex items-center gap-3 flex-wrap">
                <GraduationCap className="h-5 w-5 text-[#1E9E2A]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800">{i.title}</div>
                  <div className="text-xs text-slate-400">
                    {i.expires_at ? `Acesso até ${fmtDate(i.expires_at)}` : "Sem prazo"}
                    {i.direct ? " · Direta" : ""}{i.groups.length ? ` · Turma: ${i.groups.join(", ")}` : ""}
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/matriculas/${p.id}/${i.course_id}`)} data-testid={`enr-detail-${i.course_id}`}>Detalhes</Button>
              </div>
            );
          })}
        </div>
      )}
      <EnrollmentsDialog student={open ? { id: p.id, name: p.name } : null} onClose={() => { setOpen(false); load(); }} onChanged={load} />
    </Card>
  );
}

const dt = (iso) => { const d = new Date(iso); return !iso || isNaN(d) ? "—" : d.toLocaleDateString("pt-BR"); };
const Sub = ({ children }) => <p className="text-sm text-slate-500 -mt-2 mb-4">{children}</p>;
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const CH_ST = { pendente: ["Pendente", "bg-amber-50 text-amber-700"], paga: ["Paga", "bg-green-50 text-green-700"], vencida: ["Vencida", "bg-red-50 text-red-700"],
  cancelada: ["Cancelada", "bg-slate-100 text-slate-600"], estornada: ["Estornada", "bg-purple-50 text-purple-700"],
  approved: ["Paga", "bg-green-50 text-green-700"], pending: ["Aguardando", "bg-amber-50 text-amber-700"] };
const Pill = ({ s }) => { const [l, c] = CH_ST[s] || [s, "bg-slate-100 text-slate-600"]; return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${c}`}>{l}</span>; };

/** Página da professora no admin: links de venda e cobranças no nome dela (o que ela vê na área dela). */
function PersonSelling({ kind, id, mode }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/people/${kind}/${id}/selling`).then((r) => setD(r.data)).catch(() => setD({ links: [], sales: [], charges: [] })); }, [kind, id]);
  if (!d) return <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const copy = (u) => { navigator.clipboard.writeText(u); toast.success("Link copiado."); };
  if (mode === "vendas") return (
    <>
      <Card title="Links dela">
        <Sub>Criados quando ela abre a aba "Links de venda". Os cursos com link são os que você liga em Financeiro → Links de venda.</Sub>
        {d.links.length === 0 ? <p className="text-sm text-slate-500">Ela ainda não abriu os links de venda (ou nenhum curso dela tem link ligado).</p> : (
          <div className="divide-y divide-slate-100">{d.links.map((l) => (
            <div key={l.url} className="py-2.5 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]"><div className="font-medium text-slate-800">{l.course}</div><div className="text-xs text-slate-500">valor dela: {brl(l.price)}</div></div>
              <Button size="sm" variant="outline" onClick={() => copy(l.url)}><Copy className="h-4 w-4 mr-1" /> Copiar</Button>
              <Button size="sm" variant="outline" onClick={() => window.open(l.url, "_blank")}><ExternalLink className="h-4 w-4" /></Button>
            </div>))}</div>
        )}
      </Card>
      <Card title="Vendas pelos links dela">
        {d.sales.length === 0 ? <p className="text-sm text-slate-500">Nenhuma venda ainda.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
            {d.sales.map((v, i) => <tr key={i}><td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap">{dt(v.date)}</td><td className="py-2.5 pr-3"><div className="font-medium text-slate-800">{v.name}</div><div className="text-xs text-slate-500">{v.course}</div></td><td className="py-2.5 pr-3 text-right">{brl(v.amount)}</td><td className="py-2.5"><Pill s={v.status} /></td></tr>)}
          </tbody></table></div>
        )}
      </Card>
    </>
  );
  const tot = (st) => d.charges.filter((c) => c.status === st).reduce((a, c) => a + c.amount, 0);
  return (
    <Card title="Cobranças geradas por ela">
      <Sub>Pendentes {brl(tot("pendente"))} · Vencidas {brl(tot("vencida"))} · Pagas {brl(tot("paga"))}. Todas também aparecem em Financeiro → Cobranças.</Sub>
      {d.charges.length === 0 ? <p className="text-sm text-slate-500">Nenhuma cobrança ainda.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
          {d.charges.map((c) => <tr key={c.id}><td className="py-2.5 pr-3 text-slate-500 whitespace-nowrap">{dt(c.created_at)}</td><td className="py-2.5 pr-3"><div className="font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-500">{c.description}</div></td><td className="py-2.5 pr-3 text-right">{brl(c.amount)}</td><td className="py-2.5"><Pill s={c.status} /></td></tr>)}
        </tbody></table></div>
      )}
    </Card>
  );
}
